import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {WorksheetTaskStatusType} from 'notebook/worksheets/WorksheetConstants';
import {GRAY_DARK, GREEN, ORANGE, RED} from 'ui-library/Colors';
import BenchlingDuotoneIcon from 'ui-library/icons/BenchlingDuotoneIcon';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

interface WorksheetStepStatusIconProps {
  style?: object;
  status: WorksheetTaskStatusType;
  viewer?: boolean;
}

export default function WorksheetStepStatusIcon(props: WorksheetStepStatusIconProps): JSX.Element {
  const printableClassName = props.viewer && ['task-status-icon', props.status].join(' ');
  if (TASK_STATUS_ICONS[props.status]?.loading) {
    return (
      <div className={[css(props.style), printableClassName].join(' ')}>
        <BenchlingDuotoneIcon
          icon="spinner-third"
          primaryColor={Color.BLUE_400}
          secondaryColor={Color.BLUE_100}
          spin={true}
        />
      </div>
    );
  }

  return (
    <BenchlingIcon
      icon={TASK_STATUS_ICONS[props.status]?.icon ?? 'request-pending'}
      className={[
        css([styles.taskStatusIcon, TASK_STATUS_ICONS[props.status]?.style, props.style]),
        printableClassName,
      ].join(' ')}
    />
  );
}

const TASK_STATUS_COLORS: Record<WorksheetTaskStatusType, {color: string}> = {
  PENDING: {
    color: GRAY_DARK,
  },
  FAILED: {
    color: RED,
  },
  SKIPPED: {
    color: ORANGE,
  },
  COMPLETED: {
    color: GREEN,
  },
  READY: {
    color: GRAY_DARK,
  },
  IN_PROGRESS: {
    color: GRAY_DARK,
  },
  NOT_READY: {
    color: GRAY_DARK,
  },
  FAILING: {
    color: GRAY_DARK,
  },
  COMPLETING: {
    color: GRAY_DARK,
  },
  SKIPPING: {
    color: GRAY_DARK,
  },
  STARTING: {
    color: GRAY_DARK,
  },
  AMENDING: {
    color: GRAY_DARK,
  },
};

const styles = StyleSheet.create({
  taskStatusIcon: {
    height: '12px',
    width: '12px',
  },
  ...TASK_STATUS_COLORS,
});

const TASK_STATUS_ICONS: Record<
  WorksheetTaskStatusType,
  {icon: BenchlingIconName; name: string; style: object; loading?: boolean}
> = {
  [WorksheetTaskStatusType.PENDING]: {
    icon: 'request-pending',
    name: 'Pending',
    style: styles.PENDING,
  },
  [WorksheetTaskStatusType.READY]: {
    icon: 'request-pending',
    name: 'Pending',
    style: styles.READY,
  },
  [WorksheetTaskStatusType.NOT_READY]: {
    icon: 'lock',
    name: 'Not Ready',
    style: styles.NOT_READY,
  },
  [WorksheetTaskStatusType.FAILED]: {
    icon: 'status-failed',
    name: 'Failed',
    style: styles.FAILED,
  },
  [WorksheetTaskStatusType.SKIPPED]: {
    icon: 'forward-step',
    name: 'Skipped',
    style: styles.SKIPPED,
  },
  [WorksheetTaskStatusType.COMPLETED]: {
    icon: 'status-success',
    name: 'Completed',
    style: styles.COMPLETED,
  },
  [WorksheetTaskStatusType.IN_PROGRESS]: {
    icon: 'request-pending',
    name: 'In Progress',
    style: styles.IN_PROGRESS,
  },
  [WorksheetTaskStatusType.FAILING]: {
    icon: 'status-failed',
    name: 'Failing',
    style: styles.FAILING,
    loading: true,
  },
  [WorksheetTaskStatusType.STARTING]: {
    icon: 'request-pending',
    name: 'Starting',
    style: styles.STARTING,
  },
  [WorksheetTaskStatusType.COMPLETING]: {
    icon: 'status-success',
    name: 'Completing',
    style: styles.COMPLETING,
    loading: true,
  },
  [WorksheetTaskStatusType.SKIPPING]: {
    icon: 'forward-step',
    name: 'Skipping',
    style: styles.SKIPPING,
    loading: true,
  },
  [WorksheetTaskStatusType.AMENDING]: {
    icon: 'request-pending',
    name: 'Skipping',
    style: styles.SKIPPING,
    loading: true,
  },
};
